import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Building2,
  DollarSign,
  Activity,
  Brain,
  Shield,
  LogOut,
  Crown,
  Users,
  ClipboardList,
  Settings,
  Radio,
  Heart,
  Flag,
  Layers,
  Hammer,
  MessageSquare,
  KeyRound,
  Tags,
  Key,
  BarChart3,
  Gift,
  UsersRound,
  UserCheck,
  HandCoins,
  Scale,
} from "lucide-react";
import { cn } from "@/lib/utils";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/tenants", label: "Tenants", icon: Building2 },
  { path: "/health", label: "Health & Churn", icon: Heart },
  { path: "/cohorts", label: "Cohortes & Churn", icon: Layers },
  { path: "/revenue", label: "Revenue", icon: DollarSign },
  { path: "/platform-health", label: "Santé plateforme", icon: Activity },
  { path: "/ai-usage", label: "AI Usage", icon: Brain },
  { path: "/ai-costs", label: "Coûts IA", icon: DollarSign },
  { path: "/travauxbtp", label: "TravauxBTP", icon: Hammer },
  { path: "/broadcast", label: "Broadcast", icon: Radio },
  { path: "/reviews", label: "Modération avis", icon: MessageSquare },
  { path: "/referrals", label: "Parrainages", icon: Gift },
  { path: "/feature-flags", label: "Feature Flags", icon: Flag },
  { path: "/categories", label: "Catégories", icon: Tags },
  { path: "/api-keys", label: "Clés API", icon: Key },
  { path: "/pricing", label: "Pricing", icon: DollarSign },
  { path: "/system-settings", label: "Paramètres système", icon: Settings },
  { path: "/system-analytics", label: "Analytics système", icon: BarChart3 },
  { path: "/rgpd", label: "RGPD", icon: Shield },
  { path: "/access", label: "Accès apps", icon: KeyRound },
  { path: "/staff", label: "Staff", icon: Users },
  { path: "/internal-accounts", label: "Comptes internes", icon: UsersRound },
  { path: "/freelances", label: "Freelances prospection", icon: UserCheck },
  { path: "/commissions", label: "Commissions freelances", icon: HandCoins },
  { path: "/bareme-commissions", label: "Barème commissions", icon: Scale },
  { path: "/audit", label: "Audit Trail", icon: ClipboardList },
];

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-sidebar border-r border-sidebar-border">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-primary">
              <Crown className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <div className="font-semibold text-sm">Constrigo Admin</div>
              <div className="text-xs text-muted-foreground">Tour de contrôle</div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider mb-2 px-3">
              Supervision
            </div>
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive =
                  item.path === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* User section */}
          <div className="border-t border-sidebar-border p-4">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-primary text-primary-foreground text-sm font-semibold">
                {user?.email?.[0]?.toUpperCase() || "S"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.email}</div>
                <div className="text-xs text-muted-foreground">Super Admin</div>
              </div>
            </div>
            <Button variant="ghost" className="w-full justify-start" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="ml-64">
        <main key={location.pathname} className="p-8 animate-slide-up">
          {children}
        </main>
      </div>
    </div>
  );
}
